import { useMemo } from "react";
import { Card, Text } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { TaxIdType } from "~/graphql/supergraphEnums";
import EntityFormFields from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields";
import { VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { BackButtonWithinForm } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/BackButtonWithinForm";
import {
  FIELD_MAPPING,
  INITIAL_VALUES,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/constants";
import { getSchema } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/schema";
import { type FormValues } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/types";
import {
  mergeExtractedData,
  parseExtractedDate,
  toWizardAddress,
} from "~/pages/Compliance/InvestorCaseNavigation/InvestorCaseWizard/ProfileStep/utils";
import { AML_ENTITY_TYPE_NAME_TO_ID } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { toISOFormat } from "~/utils/datetime";
import { useEntityClientWizard } from "../../context";
import { ProfileFormFooter } from "../ProfileFormFooter";
import { resolveProfileEntityName } from "../utils";

interface Props {
  onBack: () => void;
  extractedData?: Record<string, string> | null;
  documentId?: number;
}

export const EntityProfileForm = ({ onBack, extractedData, documentId }: Props) => {
  const {
    setNewProfileData,
    setProfileDraft,
    goNext,
    profileDraft,
    productLine: linkedProductLine,
    caseInfo,
    caseType,
  } = useEntityClientWizard();

  const isPrefilled = Boolean(extractedData) || Boolean(documentId);
  const validationSchema = useMemo(() => getSchema(false, true), []);

  const draftValues = profileDraft?.values as Partial<FormValues> | undefined;

  const initialValues = useMemo(() => {
    const merged = {
      ...mergeExtractedData(INITIAL_VALUES, FIELD_MAPPING, extractedData),
      ...draftValues,
    };
    return {
      ...merged,
      entityName: resolveProfileEntityName(merged.entityName, caseType, caseInfo),
      dateOfFormation: parseExtractedDate(merged.dateOfFormation),
    };
  }, [extractedData, draftValues, caseType, caseInfo]);

  const handleSubmit = (values: Partial<FormValues>) => {
    setProfileDraft({
      values,
      documentId,
      isPrefilled,
    });
    setNewProfileData({
      accountType: values.entityType
        ? VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE[values.entityType]
        : undefined,
      creationMethod: isPrefilled ? "document" : "manual",
      documentId,
      legalBusinessName: values.entityName,
      dateOfFormation: values.dateOfFormation ? toISOFormat(values.dateOfFormation) : undefined,
      taxId: values.taxId,
      taxIdType: TaxIdType.Ein,
      address: toWizardAddress(values),
      amlEntityId: values.amlEntityType
        ? AML_ENTITY_TYPE_NAME_TO_ID[values.amlEntityType]
        : undefined,
      registrationNumber: values.registrationNumber,
      productLine: linkedProductLine,
      natureOfBusiness: values.natureOfBusiness,
      principalPlaceOfBusinessId: values.principalPlaceOfBusiness,
    });
    goNext();
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      submitOnlyChangedValues={false}
    >
      <Card>
        <BackButtonWithinForm onClick={onBack} displayText="Profile: Create method" />
        <Text marginBottom="md" variant="h3">
          New entity
        </Text>
        <EntityFormFields
          showOnboardingRootFields
          productLine={linkedProductLine}
          aiExtractionSupported={isPrefilled}
        />
      </Card>
      <ProfileFormFooter onBack={onBack} />
    </Form>
  );
};
